import React, { useState, useCallback, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../lib/utils";

/**
 * CollapsibleSection — thin wrapper used across Settings pages and the
 * Dashboard so every card can hide/expand independently. User's expand /
 * collapse state persists in localStorage under
 * `<storagePrefix><id>` and survives across sessions.
 *
 * Props:
 *  - storagePrefix: key namespace (default `ucv:settings:collapsed:`).
 *  - revealAt: when the parent bumps this ISO timestamp, any collapsed state
 *    older than the timestamp is invalidated and the section snaps back open.
 *    Used to let admins force-reveal the dashboard Featured Article for
 *    every user by toggling the global setting.
 *  - dense: compact header (used on the Dashboard).
 */
export default function CollapsibleSection({
  id,
  title,
  description,
  icon: Icon,
  defaultOpen = false,
  storagePrefix = "ucv:settings:collapsed:",
  revealAt = null,
  dense = false,
  rightSlot = null,
  children,
}) {
  const storageKey = `${storagePrefix}${id}`;
  const stampKey = `${storageKey}:ts`;

  const readStored = useCallback(() => {
    try {
      const v = localStorage.getItem(storageKey);
      if (v === null) return defaultOpen;
      // If a reveal token is newer than the stored change timestamp, ignore it
      // and snap back to open — the admin has re-published this section.
      if (revealAt) {
        const ts = localStorage.getItem(stampKey) || "";
        if (ts && ts < revealAt) return true;
      }
      return v === "1";
    } catch { return defaultOpen; }
  }, [storageKey, stampKey, defaultOpen, revealAt]);

  const [open, setOpen] = useState(readStored);

  // If the reveal timestamp changes while the app is running, re-evaluate.
  useEffect(() => { setOpen(readStored()); }, [readStored]);

  const toggle = useCallback(() => {
    setOpen((v) => {
      const next = !v;
      try {
        localStorage.setItem(storageKey, next ? "1" : "0");
        localStorage.setItem(stampKey, new Date().toISOString());
      } catch { /* private mode */ }
      return next;
    });
  }, [storageKey, stampKey]);

  return (
    <div className={cn(
      "rounded-xl border border-border bg-card overflow-hidden transition-shadow",
      open && "shadow-sm",
    )}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        data-testid={`settings-section-toggle-${id}`}
        className={cn(
          "w-full flex items-center justify-between gap-3 text-left hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
          dense ? "px-3 py-2" : "px-4 py-3",
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          {Icon && (
            <div className={cn(
              "rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0",
              dense ? "w-7 h-7" : "w-8 h-8",
            )}>
              <Icon className={cn("text-primary", dense ? "w-3.5 h-3.5" : "w-4 h-4")} />
            </div>
          )}
          <div className="min-w-0">
            <div className={cn("font-semibold truncate", dense ? "text-[13.5px]" : "text-[14px]")}>{title}</div>
            {description && (
              <div className={cn("text-muted-foreground truncate", dense ? "text-[11.5px]" : "text-[12px]")}>{description}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {rightSlot && (
            <span onClick={(e) => e.stopPropagation()} className="inline-flex items-center">{rightSlot}</span>
          )}
          <ChevronDown className={cn(
            "w-4 h-4 shrink-0 text-muted-foreground transition-transform duration-200",
            open && "rotate-180",
          )} />
        </div>
      </button>
      {open && (
        <div className={cn("border-t border-border", dense ? "p-3" : "p-4")} data-testid={`settings-section-body-${id}`}>
          {children}
        </div>
      )}
    </div>
  );
}
